import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';
import { HTTP_RES_ERROR_CODE } from '@fitness-force/enum';
import { catchError, Observable, throwError, switchMap } from 'rxjs';
import { AuthSevices } from './auth/auth.service';

// Client-side only marker (never serialized over the wire, so no CORS preflight impact).
// Flags a request that has already been retried once after a token refresh, so a
// second auth challenge on the same request cannot trigger another refresh/retry cycle.
export const AUTH_RETRIED = new HttpContextToken<boolean>(() => false);

@Injectable()
export class IntercepterService implements HttpInterceptor{
  private router = inject(Router);
  private injector = inject(Injector);

  publicApis: Array<string>;

  constructor() {
    this.publicApis= CONST_URL_SEGEMENTS.PUBLIC_APIS;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const authService = this.injector.get(AuthSevices);

    // Skip interceptor for requests with skip header
    if (req.headers.get('skip')) {
      return next.handle(req);
    }

    // Skip interceptor completely if logout is in progress
    if (authService.isLoggingOut) {
      if (req.url.includes('/auth/revoke') || req.url.includes('/auth/logout')) {
        return next.handle(req);
      }
      return next.handle(req).pipe(
        catchError((err: any) => throwError(() => err))
      );
    }

    if (this.validInterceptor(req.url)) {
      const token = authService.tokenFromStorage ?? '';
      const tokenizedReq = req.clone({
        setHeaders: {
          'Content-Type': req.method.toLowerCase() === 'patch'
            ? 'application/json-patch+json'
            : 'application/json',
          'authorization': `Bearer ${token}`,
        }
      });

      return next.handle(tokenizedReq).pipe(
        catchError((err: any) => this.handleAuthError(err, tokenizedReq, next))
      );
    }

    return next.handle(req).pipe(
      catchError((err: any) => this.handleAuthError(err, req, next))
    );
  }

  private validInterceptor(requestUrl: string): boolean {
    const positionIndicator = 'fftesting.fitnessforce.com/';
    const positionIndicatorOfAssets = 'dist/'

    const position = (requestUrl.indexOf(positionIndicatorOfAssets) == -1 ) ? requestUrl.indexOf(positionIndicator) : requestUrl.indexOf(positionIndicatorOfAssets) ;
    const positionIndicatorLength = (requestUrl.indexOf(positionIndicatorOfAssets) == -1 ) ? positionIndicator.length  : positionIndicatorOfAssets.length
    if (position > 0) {

      const destination: string = requestUrl.substring(position + positionIndicatorLength);
      for (const address of this.publicApis) {
        if (new RegExp(address).test(destination)) {
          return false;
        }
      }
    }
    return true;
  }


  handleAuthError(err: HttpErrorResponse, request: HttpRequest<any>, next: HttpHandler):Observable<any> {
    const authService = this.injector.get(AuthSevices);

    if (authService.isLoggingOut) {
      return throwError(() => err);
    }

    const isAuthChallenge = err.status === HTTP_RES_ERROR_CODE.UNAUTHORIZED || err.status === 428;
    if (!isAuthChallenge) {
      // 403 and all other statuses are surfaced to the caller unchanged.
      return throwError(() => err);
    }

    // A request that already went through one refresh+retry must not start another
    // refresh cycle. Surface the endpoint error without invalidating the whole
    // session; only a rejected refresh token is authoritative for global logout.
    if (request.context.get(AUTH_RETRIED)) {
      return throwError(() => err);
    }

    const refreshToken = authService.refreshTokenFromStorage;
    if (!refreshToken || refreshToken === null || refreshToken === 'null' || refreshToken.trim() === '') {
      this.router.navigateByUrl('/auth/logout');
      return throwError(() => new Error('No refresh token available'));
    }

    // The API rejected the current access token, so refresh even when its local
    // expiry claim still appears valid (for example after server-side revocation).
    return authService.refreshAccessTokenCoalesced(true).pipe(
      switchMap((token) => {
        if (!token) {
          if (authService.shouldForceLogoutOnRefreshError()) {
            this.router.navigateByUrl('/auth/logout');
          }
          return throwError(() => new Error('Token refresh failed'));
        }
        return this.retryRequestWithNewToken(request, next, token);
      })
    );
  }

  private retryRequestWithNewToken(request: HttpRequest<any>, next: HttpHandler, token: string): Observable<HttpEvent<any>> {
    const retriedReq = request.clone({
      setHeaders: {
        'Content-Type': request.method.toLowerCase() === 'patch'
          ? 'application/json-patch+json'
          : 'application/json',
        'authorization': `Bearer ${token}`
      },
      context: request.context.set(AUTH_RETRIED, true)
    });

    // Re-run auth handling on the retry so a repeat challenge terminates via the
    // AUTH_RETRIED guard without starting another refresh cycle.
    return next.handle(retriedReq).pipe(
      catchError((retryErr: any) => this.handleAuthError(retryErr, retriedReq, next))
    );
  }

}
